/* eslint-disable react/destructuring-assignment */
import { useEffect } from 'react';

import { useAuth } from '@steelbuy/auth';

import { isAuthenticationRequiredError, restoreError } from '@steelbuy/error';
import { ListingBuyerSearchResultModel } from '@steelbuy/ts-shared';
import { LoadingSpinner } from '@steelbuy/ui-primitive';

import { ListingBuyerSearchData } from './ListingBuyerSearchData';
import {
    ListingBuyerSearchStore,
    listingBuyerSearchStoreAccessors,
    listingBuyerSearchStoreName,
} from './ListingBuyerSearchSlice';
import { createDataProviderAccessTokenLoader } from '../../util/auth/AccessTokenLoader';
import { CollectionDataProviderProps } from '../../util/provider/DataProviderProps';
import { createSearchDataProviderValue } from '../../util/provider/SearchDataProviderValue';
import { FetchStatus } from '../../util/store/FetchStatus';
import { getBaseUrl } from '../../util/urlUtils';

export const ListingBuyerSearchDataProvider = (props: CollectionDataProviderProps<ListingBuyerSearchResultModel>) => {
    const { apiMock = false, interceptRendering = !props.lazyLoad, pendingComponent = null } = props;

    const PendingComponent = pendingComponent;

    const authConsumer = useAuth();

    const dataProviderValue = createSearchDataProviderValue<ListingBuyerSearchResultModel, ListingBuyerSearchStore>(
        listingBuyerSearchStoreName,
        listingBuyerSearchStoreAccessors,
        getBaseUrl(),
        createDataProviderAccessTokenLoader(authConsumer, getBaseUrl())
    );

    const dataProviderIdle = dataProviderValue.idle();
    const fetchStatus = dataProviderValue.queryFetchStatus();
    const error = dataProviderValue.queryFetchError();

    dataProviderValue.useFetchStatusEffect([FetchStatus.FAILED], () => {
        if (error === null) {
            return;
        }
        if (isAuthenticationRequiredError(error)) {
            authConsumer.reauthenticate();
            return;
        }
        throw restoreError(error);
    });

    useEffect(() => {
        if (dataProviderIdle && !props.lazyLoad) {
            dataProviderValue.fetch(props.sortCriteria, props.filterCriteria);
        }
    }, [dataProviderIdle, props.lazyLoad]);

    if (interceptRendering) {
        switch (fetchStatus) {
            case FetchStatus.IDLE:
                return null;
            case FetchStatus.PENDING:
                if (PendingComponent !== null) {
                    return <PendingComponent />;
                }
                return <LoadingSpinner />;
            case FetchStatus.FAILED:
                return null;
        }
    }

    return (
        <ListingBuyerSearchData.Provider value={dataProviderValue}>{props.children}</ListingBuyerSearchData.Provider>
    );
};
